import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import Header from "@/components/Header";
import { Eye, EyeOff } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (res.ok) nav(res.user.role === "admin" ? "/admin" : "/dashboard");
    else setError(res.error);
  };

  return (
    <div className="min-h-screen bg-[#030A06]">
      <Header />
      <div className="max-w-md mx-auto px-6 py-20">
        <div className="border border-[#122E1C] bg-[#07140B] p-10">
          <span className="font-mono-jb text-[11px] uppercase tracking-[0.2em] text-[#00E676]">// Member access</span>
          <h1 className="font-display font-black text-4xl tracking-tighter mt-2 mb-8">Sign in</h1>

          <form onSubmit={submit} className="space-y-5">
            <div>
              <Label htmlFor="email" className="text-[10px] uppercase tracking-[0.2em] text-[#8A9F90] font-mono-jb">Email</Label>
              <Input
                id="email"
                data-testid="login-email-input"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="bg-transparent border border-[#122E1C] rounded-none mt-2 focus:border-[#00E676] focus-visible:ring-0 text-white"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-[10px] uppercase tracking-[0.2em] text-[#8A9F90] font-mono-jb">Password</Label>
              <div className="relative mt-2">
                <Input
                  id="password"
                  data-testid="login-password-input"
                  type={showPw ? "text" : "password"}
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="bg-transparent border border-[#122E1C] rounded-none focus:border-[#00E676] focus-visible:ring-0 text-white pr-10"
                />
                <button
                  type="button"
                  data-testid="login-password-toggle"
                  onClick={() => setShowPw((v) => !v)}
                  aria-label={showPw ? "Hide password" : "Show password"}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-[#8A9F90] hover:text-[#00E676] p-1"
                >
                  {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            {error && <div data-testid="login-error" className="text-sm text-[#FF3B30] border border-[#FF3B30]/30 bg-[#FF3B30]/10 p-3">{error}</div>}
            <Button data-testid="login-submit-btn" disabled={loading} type="submit" className="w-full bg-[#00E676] hover:bg-[#00C853] text-[#030A06] font-bold rounded-none py-6">
              {loading ? "Signing in…" : "Sign in"}
            </Button>
          </form>

          <div className="mt-6 text-sm text-[#8A9F90]">
            New here? <Link to="/register" className="text-[#00E676] hover:underline" data-testid="login-to-register-link">Apply as a creative →</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
